// src/components/cost-simulator/CloudCostComparePanel.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Cloud, TrendingUp, Calculator, Zap } from 'lucide-react';
import { formatCurrency, formatTokens } from '@/lib/costCalculations';
import { aiServiceModelPresets } from '@/lib/aiServiceModels';
import type { CostSimulatorConfig, MonthlyRollupEnhanced, CostComparisonResult } from '@/lib/costCalculations';

interface CloudCostComparePanelProps {
  config: CostSimulatorConfig;
  monthlyTokens: { inputTokens: number; outputTokens: number; totalTokens: number };
  monthlyRollups: MonthlyRollupEnhanced[];
  comparison: CostComparisonResult;
}

export const CloudCostComparePanel = ({
  config,
  monthlyTokens,
  monthlyRollups,
  comparison,
}: CloudCostComparePanelProps) => {
  const firstMonth = monthlyRollups[0];
  const year1Rollups = monthlyRollups.slice(0, 12);
  const year1CloudTotal = year1Rollups.reduce((sum, r) => sum + r.monthlyCloudCost, 0);

  // Calculate costs for each model
  const modelComparisons = aiServiceModelPresets
    .filter(m => m.inputCost !== undefined && m.outputCost !== undefined)
    .map(model => {
      const monthlyInputCost = (monthlyTokens.inputTokens / 1_000_000) * (model.inputCost ?? 0);
      const monthlyOutputCost = (monthlyTokens.outputTokens / 1_000_000) * (model.outputCost ?? 0);
      const monthlyTotal = monthlyInputCost + monthlyOutputCost;
      
      // Calculate 5-year with growth
      let fiveYearTotal = 0;
      const monthlyGrowthRate = Math.pow(1 + config.annualGrowthRate / 100, 1 / 12);
      for (let i = 0; i < 60; i++) {
        const growthMultiplier = Math.pow(monthlyGrowthRate, i);
        fiveYearTotal += monthlyTotal * growthMultiplier;
      }

      return {
        ...model,
        monthlyInputCost,
        monthlyOutputCost,
        monthlyTotal,
        fiveYearTotal,
      };
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Cloud className="h-4 w-4" />
          Cloud Cost Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Token Volume Section */}
        <div className="bg-primary/5 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-primary" />
            <h4 className="font-medium">Token Volume (from Sizing Engine)</h4>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{formatTokens(monthlyTokens.inputTokens)}</div>
              <div className="text-xs text-muted-foreground">Monthly Input Tokens</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{formatTokens(monthlyTokens.outputTokens)}</div>
              <div className="text-xs text-muted-foreground">Monthly Output Tokens</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{formatTokens(monthlyTokens.totalTokens)}</div>
              <div className="text-xs text-muted-foreground">Total Monthly</div>
            </div>
          </div>
        </div>

        {/* Current Model Cost Breakdown */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              <h4 className="font-medium">Cost Breakdown: {config.aiServiceModel}</h4>
            </div>
            <Badge variant="outline">Selected Model</Badge>
          </div>

          <div className="space-y-3">
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="font-mono text-sm space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Input:</span>
                  <span>
                    {formatTokens(monthlyTokens.inputTokens)} × ${config.inputCostPerMToken}/1M ={' '}
                    <strong>{formatCurrency(firstMonth?.cloudInputCost ?? 0)}</strong>/mo
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Output:</span>
                  <span>
                    {formatTokens(monthlyTokens.outputTokens)} × ${config.outputCostPerMToken}/1M ={' '}
                    <strong>{formatCurrency(firstMonth?.cloudOutputCost ?? 0)}</strong>/mo
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between items-center font-bold">
                  <span>Monthly Total:</span>
                  <span className="text-lg">{formatCurrency(firstMonth?.monthlyCloudCost ?? 0)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold">{formatCurrency(year1CloudTotal)}</div>
                <div className="text-xs text-muted-foreground">Year 1 (with {config.annualGrowthRate}% growth)</div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold">{formatCurrency(comparison.fiveYearCloudSpend)}</div>
                <div className="text-xs text-muted-foreground">5-Year Cumulative</div>
              </div>
            </div>
          </div>
        </div>

        {/* Model Comparison */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4" />
            <h4 className="font-medium">Compare Cloud Models</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {modelComparisons.map(model => (
              <div
                key={model.value}
                className={`rounded-lg p-3 border text-center ${
                  model.value === config.aiServiceModel
                    ? 'border-primary bg-primary/5'
                    : 'border-border'
                }`}
              >
                <div className="font-medium text-sm mb-2">{model.label}</div>
                <div className="text-xs text-muted-foreground mb-1">
                  ${model.inputCost}/${model.outputCost} per 1M
                </div>
                <div className="text-lg font-bold">{formatCurrency(model.monthlyTotal)}</div>
                <div className="text-xs text-muted-foreground">/month</div>
                <div className="mt-2 text-sm font-medium text-primary">
                  {formatCurrency(model.fiveYearTotal, true)} 5Y
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Growth Projection */}
        <div className="border rounded-lg p-4">
          <h4 className="font-medium mb-3">Monthly Cost Projection (First 12 Months)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4">Month</th>
                  <th className="text-right py-2 px-2">Input Tokens</th>
                  <th className="text-right py-2 px-2">Output Tokens</th>
                  <th className="text-right py-2 px-2">Cost</th>
                  <th className="text-right py-2 pl-2">Cumulative</th>
                </tr>
              </thead>
              <tbody>
                {year1Rollups.map((rollup, i) => (
                  <tr key={rollup.monthKey} className="border-b border-border/50">
                    <td className="py-2 pr-4">{rollup.monthKey}</td>
                    <td className="text-right py-2 px-2 font-mono text-xs">
                      {formatTokens(rollup.inputTokens)}
                    </td>
                    <td className="text-right py-2 px-2 font-mono text-xs">
                      {formatTokens(rollup.outputTokens)}
                    </td>
                    <td className="text-right py-2 px-2">{formatCurrency(rollup.monthlyCloudCost)}</td>
                    <td className="text-right py-2 pl-2 font-medium">{formatCurrency(rollup.cumCloudUSD)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
